import { useState, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  CheckIcon,
  BuildingOffice2Icon,
  UserIcon,
  Squares2X2Icon,
  UserGroupIcon,
  PhoneIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { status } from "../Utils";

export const ModuleFilter = ({
  onFiltersChange,
  initialFilters = {},
  disabled = false,
  acceptedStatuses = null, // If null, use all statuses. If array, only show those statuses
  hideStatusFilter = false, // If true, hide the status filter button
  searchPlaceholder = "Search...",
  filterTitle = "Filter by Status",
  moduleOptions = [],
  userOptions = [],
  projectOptions = [],
  // New props for project-specific filters
  customerOptions = [],
  contactOptions = [],
  departmentOptions = [],
  categoryOptions = [],
  categoryTree = [], // Tree structure for categories (with children)
  brandOptions = [],
  // Custom checkbox button filters (toggle buttons)
  customCheckboxButtonFilters = [], // Array of custom checkbox button filter objects
  // Custom dropdown filters (like status filters)
  customDropdownFilters = [], // Array of custom dropdown filter objects
  // Custom range filters (min/max numeric values)
  customRangeFilters = [], // Array of custom range filter objects: { key, label, icon, minPlaceholder, maxPlaceholder }
  // Action bar to show when items are selected
  actionBar = null,
  selectedItems = [],
  /** When set, replaces the default "Users" label on the user filter button */
  usersFilterLabel = null,
  /** Segmented control above filters: `{ value, onChange, options: [{ value, label }] }` */
  segmentedControl = null,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [openFilterType, setOpenFilterType] = useState(null); // 'status', 'modules', 'users', 'projects', 'customers', 'contacts', 'departments'
  const [searchQuery, setSearchQuery] = useState(initialFilters.query || "");
  const isSyncingRef = useRef(false);
  const searchInputRef = useRef(null);
  const isSearchInputFocusedRef = useRef(false);
  const lastSyncedQueryRef = useRef(initialFilters.query || "");
  const userActionRef = useRef(false); // Track if user is actively changing filters
  const lastSyncedFiltersRef = useRef(null); // Track last synced filter values to prevent unnecessary updates
  const [filterSearchQuery, setFilterSearchQuery] = useState(""); // For searching within filter options
  const [expandedCategoryIds, setExpandedCategoryIds] = useState([]); // Track expanded categories in treeview
  const [selectedStatuses, setSelectedStatuses] = useState(
    initialFilters.statuses || []
  );
  const [selectedModules, setSelectedModules] = useState(
    initialFilters.modules || []
  );
  const [selectedUsers, setSelectedUsers] = useState(
    initialFilters.users || []
  );
  const [selectedProjects, setSelectedProjects] = useState(
    initialFilters.projects || []
  );
  // New state for project-specific filters
  const [selectedCustomers, setSelectedCustomers] = useState(
    initialFilters.customers || []
  );
  const [selectedContacts, setSelectedContacts] = useState(
    initialFilters.contacts || []
  );
  const [selectedDepartments, setSelectedDepartments] = useState(
    initialFilters.departments || []
  );
  const [selectedCategories, setSelectedCategories] = useState(
    initialFilters.categories || []
  );
  const [selectedBrands, setSelectedBrands] = useState(
    initialFilters.brands || []
  );

  // Custom checkbox button filter states
  const [customCheckboxButtonStates, setCustomCheckboxButtonStates] = useState(
    () => {
      const states = {};
      customCheckboxButtonFilters.forEach((filter) => {
        states[filter.key] =
          initialFilters[filter.key] || filter.defaultValue || false;
      });
      return states;
    }
  );

  // Custom dropdown filter states
  const [customDropdownStates, setCustomDropdownStates] = useState(() => {
    const states = {};
    customDropdownFilters.forEach((filter) => {
      const value = initialFilters[filter.key];
      // Ensure it's always an array
      states[filter.key] = Array.isArray(value) ? value : (value ? [value] : []);
    });
    return states;
  });

  // Custom range filter states
  const [customRangeStates, setCustomRangeStates] = useState(() => {
    const states = {};
    customRangeFilters.forEach((filter) => {
      const minKey = `${filter.key}_min`;
      const maxKey = `${filter.key}_max`;
      states[minKey] = initialFilters[minKey] || "";
      states[maxKey] = initialFilters[maxKey] || "";
    });
    return states;
  });

  // Temporary states for dialogs
  const [tempStatuses, setTempStatuses] = useState(
    initialFilters.statuses || []
  );
  const [tempModules, setTempModules] = useState(initialFilters.modules || []);
  const [tempUsers, setTempUsers] = useState(initialFilters.users || []);
  const [tempProjects, setTempProjects] = useState(
    initialFilters.projects || []
  );
  // New temporary states for project-specific filters
  const [tempCustomers, setTempCustomers] = useState(
    initialFilters.customers || []
  );
  const [tempContacts, setTempContacts] = useState(
    initialFilters.contacts || []
  );
  const [tempDepartments, setTempDepartments] = useState(
    initialFilters.departments || []
  );
  const [tempCategories, setTempCategories] = useState(
    initialFilters.categories || []
  );
  const [tempBrands, setTempBrands] = useState(initialFilters.brands || []);

  // Temporary states for custom dropdown filters
  const [tempCustomDropdownStates, setTempCustomDropdownStates] = useState(
    () => {
      const states = {};
      customDropdownFilters.forEach((filter) => {
        const value = initialFilters[filter.key];
        // Ensure it's always an array
        states[filter.key] = Array.isArray(value) ? value : (value ? [value] : []);
      });
      return states;
    }
  );

  // Get status options based on acceptedStatuses prop
  const getAllStatusOptions = () => {
    return Object.entries(status).map(([key, value]) => ({
      value: key,
      label: value.label,
      class: value.class,
    }));
  };

  const statusOptions = acceptedStatuses
    ? getAllStatusOptions().filter((option) =>
        acceptedStatuses.includes(option.value)
      )
    : getAllStatusOptions();

  const hasActiveFilters =
    searchQuery ||
    selectedStatuses.length > 0 ||
    selectedModules.length > 0 ||
    selectedUsers.length > 0 ||
    selectedProjects.length > 0 ||
    selectedCustomers.length > 0 ||
    selectedContacts.length > 0 ||
    selectedDepartments.length > 0 ||
    selectedCategories.length > 0 ||
    selectedBrands.length > 0 ||
    Object.values(customCheckboxButtonStates).some((value) => value === true) ||
    Object.values(customDropdownStates).some((value) => value.length > 0) ||
    Object.values(customRangeStates).some((value) => value !== "");

  // Sync internal state with initialFilters when they change
  useEffect(() => {
    // Don't sync if user is actively making changes
    if (userActionRef.current) {
      return;
    }
    
    // Check if initialFilters has actually changed by comparing with last synced values
    // Also include customDropdownFilters, customCheckboxButtonFilters, and customRangeFilters in comparison
    // to detect if their structure has changed
    const filtersString = JSON.stringify({
      initialFilters,
      customDropdownFilters: customDropdownFilters.map(f => ({ key: f.key, optionsCount: f.options?.length || 0 })),
      customCheckboxButtonFilters: customCheckboxButtonFilters.map(f => ({ key: f.key })),
      customRangeFilters: customRangeFilters.map(f => ({ key: f.key })),
    });
    
    // On first render, lastSyncedFiltersRef.current will be null, so we need to sync
    // On subsequent renders, only sync if filters have actually changed
    if (lastSyncedFiltersRef.current !== null && lastSyncedFiltersRef.current === filtersString) {
      // No changes, skip syncing
      return;
    }
    
    // Mark that we're syncing to prevent triggering applyFilters
    isSyncingRef.current = true;
    
    // NEVER sync searchQuery if input is focused - user is actively typing
    // Only sync searchQuery if:
    // 1. User is not actively typing (input not focused)
    // 2. The query value has actually changed from what we last synced
    const newQuery = initialFilters.query || "";
    const queryChanged = newQuery !== lastSyncedQueryRef.current;
    
    if (!isSearchInputFocusedRef.current && queryChanged) {
      setSearchQuery(newQuery);
      lastSyncedQueryRef.current = newQuery;
    } else if (!isSearchInputFocusedRef.current) {
      // If not focused but value hasn't changed, still update the ref
      lastSyncedQueryRef.current = newQuery;
    }
    // If input is focused, don't sync at all - preserve what user is typing
    
    // Update state values - React will handle batching and only re-render if values changed
    setSelectedStatuses(initialFilters.statuses || []);
    setSelectedModules(initialFilters.modules || []);
    setSelectedUsers(initialFilters.users || []);
    setSelectedProjects(initialFilters.projects || []);
    setSelectedCustomers(initialFilters.customers || []);
    setSelectedContacts(initialFilters.contacts || []);
    setSelectedDepartments(initialFilters.departments || []);
    setSelectedCategories(initialFilters.categories || []);
    setSelectedBrands(initialFilters.brands || []);
    
    // Update custom dropdown states
    const newCustomDropdownStates = {};
    customDropdownFilters.forEach((filter) => {
      const value = initialFilters[filter.key];
      // Ensure it's always an array
      newCustomDropdownStates[filter.key] = Array.isArray(value) ? value : (value ? [value] : []);
    });
    setCustomDropdownStates(newCustomDropdownStates);
    
    // Update custom checkbox button states
    const newCustomCheckboxButtonStates = {};
    customCheckboxButtonFilters.forEach((filter) => {
      newCustomCheckboxButtonStates[filter.key] =
        initialFilters[filter.key] !== undefined
          ? initialFilters[filter.key]
          : filter.defaultValue || false;
    });
    setCustomCheckboxButtonStates(newCustomCheckboxButtonStates);
    
    // Update custom range states
    const newCustomRangeStates = {};
    customRangeFilters.forEach((filter) => {
      const minKey = `${filter.key}_min`;
      const maxKey = `${filter.key}_max`;
      newCustomRangeStates[minKey] = initialFilters[minKey] || "";
      newCustomRangeStates[maxKey] = initialFilters[maxKey] || "";
    });
    setCustomRangeStates(newCustomRangeStates);
    
    // Store current filters as last synced
    lastSyncedFiltersRef.current = filtersString;
    
    // Reset sync flag after state updates
    setTimeout(() => {
      isSyncingRef.current = false;
    }, 0);
  }, [initialFilters, customDropdownFilters, customCheckboxButtonFilters, customRangeFilters]);

  useEffect(() => {
    // Don't apply filters if we're currently syncing from initialFilters
    if (isSyncingRef.current) {
      return;
    }
    
    // Debounce search query changes
    const timeoutId = setTimeout(() => {
      // Double-check we're not syncing before applying
      if (!isSyncingRef.current) {
        applyFilters();
        
        // Reset userActionRef after filters are applied, but only if input is not focused
        // If input is focused, keep userActionRef true to prevent syncing while user types
        if (userActionRef.current && !isSearchInputFocusedRef.current) {
          setTimeout(() => {
            userActionRef.current = false;
          }, 500);
        }
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Add useEffect to trigger applyFilters when any selection changes
  useEffect(() => {
    // Don't apply filters if we're currently syncing from initialFilters
    if (isSyncingRef.current) {
      return;
    }
    applyFilters();
    
    // Reset userActionRef after filters are applied, but only if search input is not focused
    // If search input is focused, keep userActionRef true to prevent syncing while user types
    if (userActionRef.current && !isSearchInputFocusedRef.current) {
      setTimeout(() => {
        userActionRef.current = false;
      }, 500);
    }
  }, [
    selectedStatuses,
    selectedModules,
    selectedUsers,
    selectedProjects,
    selectedCustomers,
    selectedContacts,
    selectedDepartments,
    selectedCategories,
    selectedBrands,
    customCheckboxButtonStates,
    customDropdownStates,
    customRangeStates,
  ]);

  const applyFilters = () => {
    const filters = {
      query: searchQuery,
      statuses: selectedStatuses,
      modules: selectedModules,
      users: selectedUsers,
      projects: selectedProjects,
      customers: selectedCustomers,
      contacts: selectedContacts,
      departments: selectedDepartments,
      categories: selectedCategories,
      brands: selectedBrands,
      ...customCheckboxButtonStates, // Spread custom checkbox button states
      ...customDropdownStates, // Spread custom dropdown states
      ...customRangeStates, // Spread custom range states
    };
    onFiltersChange(filters);
  };

  const handleStatusToggle = (statusValue) => {
    setTempStatuses((prev) =>
      prev.includes(statusValue)
        ? prev.filter((s) => s !== statusValue)
        : [...prev, statusValue]
    );
  };

  const handleModuleToggle = (moduleValue) => {
    setTempModules((prev) =>
      prev.includes(moduleValue)
        ? prev.filter((m) => m !== moduleValue)
        : [...prev, moduleValue]
    );
  };

  const handleUserToggle = (userValue) => {
    setTempUsers((prev) =>
      prev.includes(userValue)
        ? prev.filter((u) => u !== userValue)
        : [...prev, userValue]
    );
  };

  const handleProjectToggle = (projectValue) => {
    setTempProjects((prev) =>
      prev.includes(projectValue)
        ? prev.filter((p) => p !== projectValue)
        : [...prev, projectValue]
    );
  };

  // New handlers for project-specific filters
  const handleCustomerToggle = (customerValue) => {
    setTempCustomers((prev) =>
      prev.includes(customerValue)
        ? prev.filter((c) => c !== customerValue)
        : [...prev, customerValue]
    );
  };

  const handleContactToggle = (contactValue) => {
    setTempContacts((prev) =>
      prev.includes(contactValue)
        ? prev.filter((c) => c !== contactValue)
        : [...prev, contactValue]
    );
  };

  const handleDepartmentToggle = (departmentValue) => {
    setTempDepartments((prev) =>
      prev.includes(departmentValue)
        ? prev.filter((d) => d !== departmentValue)
        : [...prev, departmentValue]
    );
  };

  const handleCategoryToggle = (categoryValue) => {
    setTempCategories((prev) =>
      prev.includes(categoryValue)
        ? prev.filter((c) => c !== categoryValue)
        : [...prev, categoryValue]
    );
  };

  const handleBrandToggle = (brandValue) => {
    setTempBrands((prev) =>
      prev.includes(brandValue)
        ? prev.filter((b) => b !== brandValue)
        : [...prev, brandValue]
    );
  };

  // Custom checkbox button filter toggle handler
  const handleCustomCheckboxButtonToggle = (filterKey) => {
    // Mark that user is actively making a change
    userActionRef.current = true;
    
    setCustomCheckboxButtonStates((prev) => ({
      ...prev,
      [filterKey]: !prev[filterKey],
    }));
  };

  // Custom dropdown filter toggle handler
  const handleCustomDropdownToggle = (filterKey, value) => {
    setTempCustomDropdownStates((prev) => ({
      ...prev,
      [filterKey]: prev[filterKey].includes(value)
        ? prev[filterKey].filter((v) => v !== value)
        : [...prev[filterKey], value],
    }));
  };

  const handleApplyFilter = () => {
    // Mark that user is actively making a change
    userActionRef.current = true;
    
    switch (openFilterType) {
      case "status":
        setSelectedStatuses(tempStatuses);
        break;
      case "modules":
        setSelectedModules(tempModules);
        break;
      case "users":
        setSelectedUsers(tempUsers);
        break;
      case "projects":
        setSelectedProjects(tempProjects);
        break;
      case "customers":
        setSelectedCustomers(tempCustomers);
        break;
      case "contacts":
        setSelectedContacts(tempContacts);
        break;
      case "departments":
        setSelectedDepartments(tempDepartments);
        break;
      case "categories":
        setSelectedCategories(tempCategories);
        break;
      case "brands":
        setSelectedBrands(tempBrands);
        break;
      default:
        // Handle custom dropdown filters
        if (openFilterType && openFilterType.startsWith("customDropdown_")) {
          const filterKey = openFilterType.replace("customDropdown_", "");
          setCustomDropdownStates((prev) => ({
            ...prev,
            [filterKey]: tempCustomDropdownStates[filterKey],
          }));
        }
        break;
    }
    setIsOpen(false);
    setOpenFilterType(null);
    setFilterSearchQuery(""); // Clear filter search when closing
  };

  const handleResetFilter = () => {
    switch (openFilterType) {
      case "status":
        setTempStatuses(selectedStatuses);
        break;
      case "modules":
        setTempModules(selectedModules);
        break;
      case "users":
        setTempUsers(selectedUsers);
        break;
      case "projects":
        setTempProjects(selectedProjects);
        break;
      case "customers":
        setTempCustomers(selectedCustomers);
        break;
      case "contacts":
        setTempContacts(selectedContacts);
        break;
      case "departments":
        setTempDepartments(selectedDepartments);
        break;
      case "categories":
        setTempCategories(selectedCategories);
        break;
      case "brands":
        setTempBrands(selectedBrands);
        break;
      default:
        // Handle custom dropdown filters
        if (openFilterType && openFilterType.startsWith("customDropdown_")) {
          const filterKey = openFilterType.replace("customDropdown_", "");
          setTempCustomDropdownStates((prev) => ({
            ...prev,
            [filterKey]: customDropdownStates[filterKey],
          }));
        }
        break;
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    lastSyncedQueryRef.current = "";
    setSelectedStatuses([]);
    setSelectedModules([]);
    setSelectedUsers([]);
    setSelectedProjects([]);
    setSelectedCustomers([]);
    setSelectedContacts([]);
    setSelectedDepartments([]);
    setSelectedCategories([]);
    setSelectedBrands([]);
    setTempStatuses([]);
    setTempModules([]);
    setTempUsers([]);
    setTempProjects([]);
    setTempCustomers([]);
    setTempContacts([]);
    setTempDepartments([]);
    setTempCategories([]);
    setTempBrands([]);

    // Reset custom checkbox button filters to default values
    const resetCustomCheckboxButtonFilters = {};
    customCheckboxButtonFilters.forEach((filter) => {
      resetCustomCheckboxButtonFilters[filter.key] =
        filter.defaultValue || false;
    });
    setCustomCheckboxButtonStates(resetCustomCheckboxButtonFilters);

    // Reset custom dropdown filters
    const resetCustomDropdownFilters = {};
    customDropdownFilters.forEach((filter) => {
      resetCustomDropdownFilters[filter.key] = [];
    });
    setCustomDropdownStates(resetCustomDropdownFilters);

    // Reset custom range filters
    const resetCustomRangeFilters = {};
    customRangeFilters.forEach((filter) => {
      const minKey = `${filter.key}_min`;
      const maxKey = `${filter.key}_max`;
      resetCustomRangeFilters[minKey] = "";
      resetCustomRangeFilters[maxKey] = "";
    });
    setCustomRangeStates(resetCustomRangeFilters);

    onFiltersChange({
      query: "",
      statuses: [],
      modules: [],
      users: [],
      projects: [],
      customers: [],
      contacts: [],
      departments: [],
      categories: [],
      brands: [],
      ...resetCustomCheckboxButtonFilters,
      ...resetCustomDropdownFilters,
      ...resetCustomRangeFilters,
    });
  };

  const getStatusLabel = (statusValue) => {
    return status[statusValue]?.label || statusValue;
  };

  const getStatusClass = (statusValue) => {
    return status[statusValue]?.class || "bg-gray-200 text-gray-800";
  };

  const getModuleLabel = (moduleValue) => {
    const module = moduleOptions.find((m) => m.value === moduleValue);
    return module?.label || moduleValue;
  };

  const getUserLabel = (userValue) => {
    const user = userOptions.find((u) => u.value === userValue);
    return user?.label || userValue;
  };

  const getProjectLabel = (projectValue) => {
    const project = projectOptions.find((p) => p.value === projectValue);
    return project?.label || projectValue;
  };

  // New label getters for project-specific filters
  const getCustomerLabel = (customerValue) => {
    const customer = customerOptions.find((c) => c.value === customerValue);
    return customer?.label || customerValue;
  };

  const getContactLabel = (contactValue) => {
    const contact = contactOptions.find((c) => c.value === contactValue);
    return contact?.label || contactValue;
  };

  const getDepartmentLabel = (departmentValue) => {
    const department = departmentOptions.find(
      (d) => d.value === departmentValue
    );
    return department?.label || departmentValue;
  };

  const getCategoryLabel = (categoryValue) => {
    // First try to find in categoryTree (recursive search)
    const findInTree = (tree, value) => {
      for (const cat of tree) {
        if (cat.id === value || cat.id === String(value) || String(cat.id) === String(value)) {
          return cat.displayTitle || cat.title || cat.name || "";
        }
        if (cat.children && cat.children.length > 0) {
          const found = findInTree(cat.children, value);
          if (found) return found;
        }
      }
      return null;
    };
    
    if (categoryTree && categoryTree.length > 0) {
      const found = findInTree(categoryTree, categoryValue);
      if (found) return found;
    }
    
    // Fallback to categoryOptions
    if (categoryOptions && categoryOptions.length > 0) {
      const category = categoryOptions.find((c) => c.value === categoryValue);
      if (category) return category.label;
    }
    
    return categoryValue;
  };

  const getBrandLabel = (brandValue) => {
    const brand = brandOptions.find((b) => b.value === brandValue);
    return brand?.label || brandValue;
  };

  const openFilterDialog = (filterType) => {
    setOpenFilterType(filterType);
    setIsOpen(true);
    setFilterSearchQuery(""); // Clear search when opening new filter
  };

  const getFilterOptions = () => {
    let options = [];
    switch (openFilterType) {
      case "status":
        options = statusOptions;
        break;
      case "modules":
        options = moduleOptions;
        break;
      case "users":
        options = userOptions;
        break;
      case "projects":
        options = projectOptions;
        break;
      case "customers":
        options = customerOptions;
        break;
      case "contacts":
        options = contactOptions;
        break;
      case "departments":
        options = departmentOptions;
        break;
      case "categories":
        // For categories, we'll handle treeview separately
        // Return empty array since we use categoryTree for rendering
        options = categoryOptions || [];
        break;
      case "brands":
        options = brandOptions;
        break;
      default:
        // Handle custom dropdown filters
        if (openFilterType && openFilterType.startsWith("customDropdown_")) {
          const filterKey = openFilterType.replace("customDropdown_", "");
          const customFilter = customDropdownFilters.find(
            (f) => f.key === filterKey
          );
          options = customFilter ? customFilter.options : [];
        } else {
          options = [];
        }
        break;
    }

    // Filter options based on search query
    if (filterSearchQuery.trim()) {
      options = options.filter((option) =>
        option.label.toLowerCase().includes(filterSearchQuery.toLowerCase())
      );
    }

    return options;
  };

  const getTempSelected = () => {
    switch (openFilterType) {
      case "status":
        return tempStatuses;
      case "modules":
        return tempModules;
      case "users":
        return tempUsers;
      case "projects":
        return tempProjects;
      case "customers":
        return tempCustomers;
      case "contacts":
        return tempContacts;
      case "departments":
        return tempDepartments;
      case "categories":
        return tempCategories;
      case "brands":
        return tempBrands;
      default:
        // Handle custom dropdown filters
        if (openFilterType && openFilterType.startsWith("customDropdown_")) {
          const filterKey = openFilterType.replace("customDropdown_", "");
          return tempCustomDropdownStates[filterKey] || [];
        }
        return [];
    }
  };

  const handleOptionToggle = (value) => {
    switch (openFilterType) {
      case "status":
        handleStatusToggle(value);
        break;
      case "modules":
        handleModuleToggle(value);
        break;
      case "users":
        handleUserToggle(value);
        break;
      case "projects":
        handleProjectToggle(value);
        break;
      case "customers":
        handleCustomerToggle(value);
        break;
      case "contacts":
        handleContactToggle(value);
        break;
      case "departments":
        handleDepartmentToggle(value);
        break;
      case "categories":
        handleCategoryToggle(value);
        break;
      case "brands":
        handleBrandToggle(value);
        break;
      default:
        // Handle custom dropdown filters
        if (openFilterType && openFilterType.startsWith("customDropdown_")) {
          const filterKey = openFilterType.replace("customDropdown_", "");
          handleCustomDropdownToggle(filterKey, value);
        }
        break;
    }
  };

  const getFilterTitle = () => {
    switch (openFilterType) {
      case "status":
        return t("Filter by Status");
      case "modules":
        return t("Filter by Modules");
      case "users":
        return t("Filter by Users");
      case "projects":
        return t("Filter by Projects");
      case "customers":
        return t("Filter by Customers");
      case "contacts":
        return t("Filter by Contacts");
      case "departments":
        return t("Filter by Departments");
      case "categories":
        return t("Filter by Categories");
      case "brands":
        return t("Filter by Brands");
      default:
        // Handle custom dropdown filters
        if (openFilterType && openFilterType.startsWith("customDropdown_")) {
          const filterKey = openFilterType.replace("customDropdown_", "");
          const customFilter = customDropdownFilters.find(
            (f) => f.key === filterKey
          );
          return customFilter ? customFilter.label : t("Filter");
        }
        return t("Filter");
    }
  };

  const getFilterIcon = (type) => {
    switch (type) {
      case "status":
        return <FunnelIcon className="h-4 w-4 mr-2" />;
      case "modules":
        return <Squares2X2Icon className="h-4 w-4 mr-2" />;
      case "users":
        return <UserIcon className="h-4 w-4 mr-2" />;
      case "projects":
        return <BuildingOffice2Icon className="h-4 w-4 mr-2" />;
      case "customers":
        return <UserGroupIcon className="h-4 w-4 mr-2" />;
      case "contacts":
        return <PhoneIcon className="h-4 w-4 mr-2" />;
      case "departments":
        return <BuildingOffice2Icon className="h-4 w-4 mr-2" />;
      case "categories":
        return <Squares2X2Icon className="h-4 w-4 mr-2" />;
      case "brands":
        return <BuildingOffice2Icon className="h-4 w-4 mr-2" />;
      default:
        // Handle custom dropdown filters
        if (type && type.startsWith("customDropdown_")) {
          const filterKey = type.replace("customDropdown_", "");
          const customFilter = customDropdownFilters.find(
            (f) => f.key === filterKey
          );
          return customFilter && customFilter.icon ? (
            <customFilter.icon className="h-4 w-4 mr-2" />
          ) : (
            <FunnelIcon className="h-4 w-4 mr-2" />
          );
        }
        return <FunnelIcon className="h-4 w-4 mr-2" />;
    }
  };

  const getFilterLabel = (type) => {
    switch (type) {
      case "status":
        return t("Status");
      case "modules":
        return t("Modules");
      case "users":
        return usersFilterLabel || t("Users");
      case "projects":
        return t("Projects");
      case "customers":
        return t("Customers");
      case "contacts":
        return t("Contacts");
      case "departments":
        return t("Departments");
      case "categories":
        return t("Categories");
      case "brands":
        return t("Brands");
      default:
        // Handle custom dropdown filters
        if (type && type.startsWith("customDropdown_")) {
          const filterKey = type.replace("customDropdown_", "");
          const customFilter = customDropdownFilters.find(
            (f) => f.key === filterKey
          );
          return customFilter ? customFilter.label : t("Filter");
        }
        return t("Filter");
    }
  };

  const getSelectedCount = (type) => {
    switch (type) {
      case "status":
        return selectedStatuses.length;
      case "modules":
        return selectedModules.length;
      case "users":
        return selectedUsers.length;
      case "projects":
        return selectedProjects.length;
      case "customers":
        return selectedCustomers.length;
      case "contacts":
        return selectedContacts.length;
      case "departments":
        return selectedDepartments.length;
      case "categories":
        return selectedCategories.length;
      case "brands":
        return selectedBrands.length;
      default:
        // Handle custom dropdown filters
        if (type && type.startsWith("customDropdown_")) {
          const filterKey = type.replace("customDropdown_", "");
          return customDropdownStates[filterKey]
            ? customDropdownStates[filterKey].length
            : 0;
        }
        return 0;
    }
  };

  const getSelectedItems = (type) => {
    switch (type) {
      case "status":
        return selectedStatuses;
      case "modules":
        return selectedModules;
      case "users":
        return selectedUsers;
      case "projects":
        return selectedProjects;
      case "customers":
        return selectedCustomers;
      case "contacts":
        return selectedContacts;
      case "departments":
        return selectedDepartments;
      case "categories":
        return selectedCategories;
      case "brands":
        return selectedBrands;
      default:
        // Handle custom dropdown filters
        if (type && type.startsWith("customDropdown_")) {
          const filterKey = type.replace("customDropdown_", "");
          return customDropdownStates[filterKey] || [];
        }
        return [];
    }
  };

  const removeSelectedItem = (type, value) => {
    // Mark that user is actively making a change
    userActionRef.current = true;
    
    switch (type) {
      case "status":
        setSelectedStatuses((prev) => prev.filter((s) => s !== value));
        break;
      case "modules":
        setSelectedModules((prev) => prev.filter((m) => m !== value));
        break;
      case "users":
        setSelectedUsers((prev) => prev.filter((u) => u !== value));
        break;
      case "projects":
        setSelectedProjects((prev) => prev.filter((p) => p !== value));
        break;
      case "customers":
        setSelectedCustomers((prev) => prev.filter((c) => c !== value));
        break;
      case "contacts":
        setSelectedContacts((prev) => prev.filter((c) => c !== value));
        break;
      case "departments":
        setSelectedDepartments((prev) => prev.filter((d) => d !== value));
        break;
      case "categories":
        setSelectedCategories((prev) => prev.filter((c) => c !== value));
        break;
      case "brands":
        setSelectedBrands((prev) => prev.filter((b) => b !== value));
        break;
      default:
        // Handle custom dropdown filters
        if (type && type.startsWith("customDropdown_")) {
          const filterKey = type.replace("customDropdown_", "");
          setCustomDropdownStates((prev) => ({
            ...prev,
            [filterKey]: prev[filterKey].filter((v) => v !== value),
          }));
        }
        break;
    }
  };

  const getItemLabel = (type, value) => {
    switch (type) {
      case "status":
        return getStatusLabel(value);
      case "modules":
        return getModuleLabel(value);
      case "users":
        return getUserLabel(value);
      case "projects":
        return getProjectLabel(value);
      case "customers":
        return getCustomerLabel(value);
      case "contacts":
        return getContactLabel(value);
      case "departments":
        return getDepartmentLabel(value);
      case "categories":
        return getCategoryLabel(value);
      case "brands":
        return getBrandLabel(value);
      default:
        // Handle custom dropdown filters
        if (type && type.startsWith("customDropdown_")) {
          const filterKey = type.replace("customDropdown_", "");
          const customFilter = customDropdownFilters.find(
            (f) => f.key === filterKey
          );
          if (customFilter) {
            const option = customFilter.options.find((o) => o.value === value);
            return option ? option.label : value;
          }
        }
        return value;
    }
  };

  const getItemClass = (type, value) => {
    switch (type) {
      case "status":
        return getStatusClass(value);
      case "modules":
        return "bg-blue-100 text-blue-800";
      case "users":
        return "bg-green-100 text-green-800";
      case "projects":
        return "bg-purple-100 text-purple-800";
      case "customers":
        return "bg-indigo-100 text-indigo-800";
      case "contacts":
        return "bg-pink-100 text-pink-800";
      case "departments":
        return "bg-orange-100 text-orange-800";
      case "categories":
        return "bg-teal-100 text-teal-800";
      case "brands":
        return "bg-purple-100 text-purple-800";
      default:
        // Handle custom dropdown filters
        if (type && type.startsWith("customDropdown_")) {
          const filterKey = type.replace("customDropdown_", "");
          const customFilter = customDropdownFilters.find(
            (f) => f.key === filterKey
          );
          if (customFilter) {
            const option = customFilter.options.find((o) => o.value === value);
            return option ? option.class : "bg-gray-100 text-gray-800";
          }
        }
        return "bg-gray-200 text-gray-800";
    }
  };

  // Helper function to check if a category or any of its children matches search
  const categoryMatchesSearch = (category, searchQuery) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const categoryTitle = (category.displayTitle || category.title || category.name || "").toLowerCase();
    
    // Check if category itself matches
    if (categoryTitle.includes(query)) return true;
    
    // Check if any child matches
    if (category.children && category.children.length > 0) {
      return category.children.some(child => categoryMatchesSearch(child, searchQuery));
    }
    
    return false;
  };

  // Helper function to check if a category should be highlighted (matches search)
  const categoryShouldHighlight = (category, searchQuery) => {
    if (!searchQuery.trim()) return false;
    const query = searchQuery.toLowerCase();
    const categoryTitle = (category.displayTitle || category.title || category.name || "").toLowerCase();
    return categoryTitle.includes(query);
  };

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategoryIds(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Recursive component to render category tree
  const CategoryTreeItem = ({ category, level = 0, searchQuery = "" }) => {
    const isExpanded = expandedCategoryIds.includes(category.id);
    const hasChildren = category.children && category.children.length > 0;
    const matchesSearch = categoryMatchesSearch(category, searchQuery);
    const shouldHighlight = categoryShouldHighlight(category, searchQuery);
    const isSelected = getTempSelected().includes(category.id);
    
    // Auto-expand if searching and category matches - must be called before any conditional returns
    useEffect(() => {
      if (searchQuery.trim() && shouldHighlight && hasChildren && !isExpanded) {
        setExpandedCategoryIds(prev => {
          if (!prev.includes(category.id)) {
            return [...prev, category.id];
          }
          return prev;
        });
      }
    }, [searchQuery, shouldHighlight, hasChildren, isExpanded, category.id]);
    
    // If searching and this category doesn't match, don't show it (but show parent if children match)
    if (searchQuery.trim() && !matchesSearch) {
      return null;
    }

    return (
      <div>
        <label
          className={`flex cursor-pointer items-center rounded-2xl border border-slate-200 p-3 hover:bg-slate-50 ${
            shouldHighlight ? "border-amber-300 bg-amber-50" : ""
          }`}
          style={{ paddingLeft: `${level * 24 + 12}px` }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleOptionToggle(category.id)}
            className="h-5 w-5 rounded border-slate-300 text-slate-900 focus:ring-slate-400 sm:h-4 sm:w-4"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="ml-3 flex items-center flex-1">
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCategoryExpansion(category.id);
                }}
                className="flex items-center focus:outline-none"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
                <span className={`ml-2 text-base font-medium sm:text-sm ${
                  shouldHighlight ? "text-yellow-800 font-semibold" : "text-gray-900"
                }`}>
                  {category.displayTitle || category.title || category.name || ""}
                </span>
              </button>
            ) : (
              <span className={`text-base font-medium sm:text-sm ${
                shouldHighlight ? "text-yellow-800 font-semibold" : "text-gray-900"
              }`}>
                {category.displayTitle || category.title || category.name || ""}
              </span>
            )}
          </div>
          {isSelected && (
            <CheckIcon className="ml-auto h-6 w-6 text-slate-900 sm:h-5 sm:w-5" />
          )}
        </label>
        {hasChildren && isExpanded && (
          <div>
            {category.children.map((child) => (
              <CategoryTreeItem
                key={child.id}
                category={child}
                level={level + 1}
                searchQuery={searchQuery}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Determine which filter buttons to show based on available options
  const shouldShowFilter = (filterType) => {
    switch (filterType) {
      case "status":
        return statusOptions.length > 0;
      case "modules":
        return moduleOptions.length > 0;
      case "users":
        return userOptions.length > 0;
      case "projects":
        return projectOptions.length > 0;
      case "customers":
        return customerOptions.length > 0;
      case "contacts":
        return contactOptions.length > 0;
      case "departments":
        return departmentOptions.length > 0;
      case "categories":
        return (categoryTree && categoryTree.length > 0) || categoryOptions.length > 0;
      case "brands":
        return brandOptions.length > 0;
      default:
        // Handle custom dropdown filters
        if (filterType && filterType.startsWith("customDropdown_")) {
          const filterKey = filterType.replace("customDropdown_", "");
          const customFilter = customDropdownFilters.find(
            (f) => f.key === filterKey
          );
          return (
            customFilter &&
            customFilter.options &&
            customFilter.options.length > 0
          );
        }
        return false;
    }
  };

  const filterBtnBase =
    "inline-flex items-center px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-slate-200 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 rounded-full border shadow-sm";
  const filterBtnIdle =
    "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50";
  const filterBtnActive = "border-slate-900 bg-slate-900 text-white shadow-sm hover:bg-slate-800";

  return (
    <div className="space-y-5">
      {/* Action Bar - Show when items are selected */}
      {selectedItems && selectedItems.length > 0 && actionBar && (
        <div className="rounded-2xl border border-slate-200 bg-slate-900 px-5 py-3 text-sm text-white shadow-sm">
          {actionBar}
        </div>
      )}
      
      {/* Segmented control (e.g. project scope) */}
      {segmentedControl &&
        Array.isArray(segmentedControl.options) &&
        segmentedControl.options.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {segmentedControl.options.map((opt) => {
              const active = segmentedControl.value === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    segmentedControl.onChange && segmentedControl.onChange(opt.value)
                  }
                  className={`${filterBtnBase} ${
                    active ? filterBtnActive : filterBtnIdle
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

      {/* Search and Filter Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {/* Search Input */}
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
            <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            // disabled={disabled}
            value={searchQuery}
            onChange={(e) => {
              const newValue = e.target.value;
              // Mark that user is actively making a change
              userActionRef.current = true;
              setSearchQuery(newValue);
              // Update last synced ref when user types
              lastSyncedQueryRef.current = newValue;
            }}
            onFocus={() => {
              isSearchInputFocusedRef.current = true;
            }}
            onBlur={() => {
              isSearchInputFocusedRef.current = false;
              // Sync with initialFilters when user leaves the field
              const currentQuery = initialFilters.query || "";
              if (currentQuery !== searchQuery) {
                lastSyncedQueryRef.current = currentQuery;
              }
              // Reset userActionRef after a delay to allow syncing when user leaves the field
              setTimeout(() => {
                userActionRef.current = false;
              }, 200);
            }}
            placeholder={t(searchPlaceholder)}
            className="block w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-11 pr-4 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-slate-100"
          />
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          {/* Status Filter Button */}
          {!hideStatusFilter && shouldShowFilter("status") && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => openFilterDialog("status")}
              className={`${filterBtnBase} ${
                selectedStatuses.length > 0 ? filterBtnActive : filterBtnIdle
              }`}
            >
              {getFilterIcon("status")}
              {getFilterLabel("status")}
              {getSelectedCount("status") > 0 && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    selectedStatuses.length > 0
                      ? "bg-white/20 text-inherit"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {getSelectedCount("status")}
                </span>
              )}
            </button>
          )}

          {/* Modules Filter Button */}
          {shouldShowFilter("modules") && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => openFilterDialog("modules")}
              className={`${filterBtnBase} ${
                selectedModules.length > 0 ? filterBtnActive : filterBtnIdle
              }`}
            >
              {getFilterIcon("modules")}
              {getFilterLabel("modules")}
              {getSelectedCount("modules") > 0 && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    selectedModules.length > 0
                      ? "bg-white/20 text-inherit"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {getSelectedCount("modules")}
                </span>
              )}
            </button>
          )}

          {/* Users Filter Button */}
          {shouldShowFilter("users") && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => openFilterDialog("users")}
              className={`${filterBtnBase} ${
                selectedUsers.length > 0 ? filterBtnActive : filterBtnIdle
              }`}
            >
              {getFilterIcon("users")}
              {getFilterLabel("users")}
              {getSelectedCount("users") > 0 && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    selectedUsers.length > 0
                      ? "bg-white/20 text-inherit"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {getSelectedCount("users")}
                </span>
              )}
            </button>
          )}

          {/* Projects Filter Button */}
          {shouldShowFilter("projects") && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => openFilterDialog("projects")}
              className={`${filterBtnBase} ${
                selectedProjects.length > 0 ? filterBtnActive : filterBtnIdle
              }`}
            >
              {getFilterIcon("projects")}
              {getFilterLabel("projects")}
              {getSelectedCount("projects") > 0 && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    selectedProjects.length > 0
                      ? "bg-white/20 text-inherit"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {getSelectedCount("projects")}
                </span>
              )}
            </button>
          )}

          {/* Customers Filter Button */}
          {shouldShowFilter("customers") && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => openFilterDialog("customers")}
              className={`${filterBtnBase} ${
                selectedCustomers.length > 0 ? filterBtnActive : filterBtnIdle
              }`}
            >
              {getFilterIcon("customers")}
              {getFilterLabel("customers")}
              {getSelectedCount("customers") > 0 && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    selectedCustomers.length > 0
                      ? "bg-white/20 text-inherit"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {getSelectedCount("customers")}
                </span>
              )}
            </button>
          )}

          {/* Contacts Filter Button */}
          {shouldShowFilter("contacts") && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => openFilterDialog("contacts")}
              className={`${filterBtnBase} ${
                selectedContacts.length > 0 ? filterBtnActive : filterBtnIdle
              }`}
            >
              {getFilterIcon("contacts")}
              {getFilterLabel("contacts")}
              {getSelectedCount("contacts") > 0 && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    selectedContacts.length > 0
                      ? "bg-white/20 text-inherit"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {getSelectedCount("contacts")}
                </span>
              )}
            </button>
          )}

          {/* Departments Filter Button */}
          {shouldShowFilter("departments") && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => openFilterDialog("departments")}
              className={`${filterBtnBase} ${
                selectedDepartments.length > 0 ? filterBtnActive : filterBtnIdle
              }`}
            >
              {getFilterIcon("departments")}
              {getFilterLabel("departments")}
              {getSelectedCount("departments") > 0 && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    selectedDepartments.length > 0
                      ? "bg-white/20 text-inherit"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {getSelectedCount("departments")}
                </span>
              )}
            </button>
          )}

          {/* Categories Filter Button */}
          {shouldShowFilter("categories") && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => openFilterDialog("categories")}
              className={`${filterBtnBase} ${
                selectedCategories.length > 0 ? filterBtnActive : filterBtnIdle
              }`}
            >
              {getFilterIcon("categories")}
              {getFilterLabel("categories")}
              {getSelectedCount("categories") > 0 && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    selectedCategories.length > 0
                      ? "bg-white/20 text-inherit"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {getSelectedCount("categories")}
                </span>
              )}
            </button>
          )}

          {/* Brands Filter Button */}
          {shouldShowFilter("brands") && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => openFilterDialog("brands")}
              className={`${filterBtnBase} ${
                selectedBrands.length > 0 ? filterBtnActive : filterBtnIdle
              }`}
            >
              {getFilterIcon("brands")}
              {getFilterLabel("brands")}
              {getSelectedCount("brands") > 0 && (
                <span
                  className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    selectedBrands.length > 0
                      ? "bg-white/20 text-inherit"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {getSelectedCount("brands")}
                </span>
              )}
            </button>
          )}

          {/* Custom Dropdown Filter Buttons */}
          {customDropdownFilters.map((filter) => {
            const ddActive =
              customDropdownStates[filter.key] &&
              customDropdownStates[filter.key].length > 0;
            return (
              <button
                key={`customDropdown_${filter.key}`}
                type="button"
                disabled={disabled}
                onClick={() => openFilterDialog(`customDropdown_${filter.key}`)}
                className={`${filterBtnBase} ${
                  ddActive ? filterBtnActive : filterBtnIdle
                }`}
              >
                {filter.icon && <filter.icon className="h-4 w-4 mr-2" />}
                {filter.label}
                {ddActive && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-inherit">
                    {customDropdownStates[filter.key].length}
                  </span>
                )}
              </button>
            );
          })}

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              type="button"
              disabled={disabled}
              onClick={handleClearFilters}
              className="inline-flex items-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <XMarkIcon className="mr-2 h-4 w-4" />
              {t("Clear")}
            </button>
          )}
        </div>
      </div>

      {/* Custom Checkbox Button Filters - Under search bar */}
      {customCheckboxButtonFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {customCheckboxButtonFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              disabled={disabled}
              onClick={() => handleCustomCheckboxButtonToggle(filter.key)}
              className={`${filterBtnBase} ${
                customCheckboxButtonStates[filter.key]
                  ? filterBtnActive
                  : filterBtnIdle
              }`}
            >
              {filter.icon && <filter.icon className="h-4 w-4 mr-2" />}
              {filter.label}
              {customCheckboxButtonStates[filter.key] && (
                <span className="ml-2 inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-inherit">
                  ✓
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Custom Range Filters - Under checkbox buttons */}
      {customRangeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {customRangeFilters.map((filter) => {
            const minKey = `${filter.key}_min`;
            const maxKey = `${filter.key}_max`;
            const hasValue = customRangeStates[minKey] || customRangeStates[maxKey];
            return (
              <div
                key={filter.key}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold shadow-sm ${
                  hasValue
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-800"
                }`}
              >
                {filter.icon && (
                  <filter.icon
                    className={`h-4 w-4 ${hasValue ? "text-white/80" : "text-slate-600"}`}
                  />
                )}
                <span className={hasValue ? "text-white/90" : "text-slate-700"}>
                  {filter.label}:
                </span>
                <input
                  type="number"
                  step="0.01"
                  disabled={disabled}
                  value={customRangeStates[minKey] || ""}
                  onChange={(e) => {
                    userActionRef.current = true;
                    setCustomRangeStates((prev) => ({
                      ...prev,
                      [minKey]: e.target.value,
                    }));
                  }}
                  placeholder={filter.minPlaceholder || "Min"}
                  className="w-20 rounded-xl border border-slate-200 bg-white px-2 py-1 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:bg-slate-100"
                />
                <span className={hasValue ? "text-white/70" : "text-slate-500"}>
                  -
                </span>
                <input
                  type="number"
                  step="0.01"
                  disabled={disabled}
                  value={customRangeStates[maxKey] || ""}
                  onChange={(e) => {
                    userActionRef.current = true;
                    setCustomRangeStates((prev) => ({
                      ...prev,
                      [maxKey]: e.target.value,
                    }));
                  }}
                  placeholder={filter.maxPlaceholder || "Max"}
                  className="w-20 rounded-xl border border-slate-200 bg-white px-2 py-1 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200 disabled:bg-slate-100"
                />
                {hasValue && (
                  <button
                    type="button"
                    onClick={() => {
                      userActionRef.current = true;
                      setCustomRangeStates((prev) => ({
                        ...prev,
                        [minKey]: "",
                        [maxKey]: "",
                      }));
                    }}
                    className={`ml-2 focus:outline-none ${
                      hasValue
                        ? "text-white/70 hover:text-white"
                        : "text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {searchQuery && (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-800">
              {t("Search")}: {searchQuery}
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  lastSyncedQueryRef.current = "";
                }}
                className="ml-2 inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-800 focus:outline-none"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          )}

          {/* Status Filters */}
          {selectedStatuses.map((statusValue) => (
            <span
              key={`status-${statusValue}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getItemClass(
                "status",
                statusValue
              )}`}
            >
              {getItemLabel("status", statusValue)}
              <button
                type="button"
                onClick={() => removeSelectedItem("status", statusValue)}
                className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}

          {/* Module Filters */}
          {selectedModules.map((moduleValue) => (
            <span
              key={`module-${moduleValue}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getItemClass(
                "modules",
                moduleValue
              )}`}
            >
              {getItemLabel("modules", moduleValue)}
              <button
                type="button"
                onClick={() => removeSelectedItem("modules", moduleValue)}
                className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}

          {/* User Filters */}
          {selectedUsers.map((userValue) => (
            <span
              key={`user-${userValue}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getItemClass(
                "users",
                userValue
              )}`}
            >
              {getItemLabel("users", userValue)}
              <button
                type="button"
                onClick={() => removeSelectedItem("users", userValue)}
                className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}

          {/* Project Filters */}
          {selectedProjects.map((projectValue) => (
            <span
              key={`project-${projectValue}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getItemClass(
                "projects",
                projectValue
              )}`}
            >
              {getItemLabel("projects", projectValue)}
              <button
                type="button"
                onClick={() => removeSelectedItem("projects", projectValue)}
                className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}

          {/* Customer Filters */}
          {selectedCustomers.map((customerValue) => (
            <span
              key={`customer-${customerValue}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getItemClass(
                "customers",
                customerValue
              )}`}
            >
              {getItemLabel("customers", customerValue)}
              <button
                type="button"
                onClick={() => removeSelectedItem("customers", customerValue)}
                className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}

          {/* Contact Filters */}
          {selectedContacts.map((contactValue) => (
            <span
              key={`contact-${contactValue}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getItemClass(
                "contacts",
                contactValue
              )}`}
            >
              {getItemLabel("contacts", contactValue)}
              <button
                type="button"
                onClick={() => removeSelectedItem("contacts", contactValue)}
                className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}

          {/* Department Filters */}
          {selectedDepartments.map((departmentValue) => (
            <span
              key={`department-${departmentValue}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getItemClass(
                "departments",
                departmentValue
              )}`}
            >
              {getItemLabel("departments", departmentValue)}
              <button
                type="button"
                onClick={() =>
                  removeSelectedItem("departments", departmentValue)
                }
                className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}

          {/* Category Filters */}
          {selectedCategories.map((categoryValue) => (
            <span
              key={`category-${categoryValue}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getItemClass(
                "categories",
                categoryValue
              )}`}
            >
              {getItemLabel("categories", categoryValue)}
              <button
                type="button"
                onClick={() => removeSelectedItem("categories", categoryValue)}
                className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}

          {/* Brand Filters */}
          {selectedBrands.map((brandValue) => (
            <span
              key={`brand-${brandValue}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getItemClass(
                "brands",
                brandValue
              )}`}
            >
              {getItemLabel("brands", brandValue)}
              <button
                type="button"
                onClick={() => removeSelectedItem("brands", brandValue)}
                className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </span>
          ))}

          {/* Custom Dropdown Filter Tags */}
          {customDropdownFilters.map(
            (filter) =>
              customDropdownStates[filter.key] &&
              Array.isArray(customDropdownStates[filter.key]) &&
              customDropdownStates[filter.key].map((value) => (
                <span
                  key={`customDropdown-${filter.key}-${value}`}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getItemClass(
                    `customDropdown_${filter.key}`,
                    value
                  )}`}
                >
                  {getItemLabel(`customDropdown_${filter.key}`, value)}
                  <button
                    type="button"
                    onClick={() =>
                      removeSelectedItem(`customDropdown_${filter.key}`, value)
                    }
                    className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))
          )}

          {/* Custom Range Filter Tags */}
          {customRangeFilters.map((filter) => {
            const minKey = `${filter.key}_min`;
            const maxKey = `${filter.key}_max`;
            const minValue = customRangeStates[minKey];
            const maxValue = customRangeStates[maxKey];
            if (!minValue && !maxValue) return null;
            
            const rangeLabel = minValue && maxValue
              ? `${filter.label}: ${minValue} - ${maxValue}`
              : minValue
              ? `${filter.label}: ≥ ${minValue}`
              : `${filter.label}: ≤ ${maxValue}`;
            
            return (
              <span
                key={`customRange-${filter.key}`}
                className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-800"
              >
                {rangeLabel}
                <button
                  type="button"
                  onClick={() => {
                    userActionRef.current = true;
                    setCustomRangeStates((prev) => ({
                      ...prev,
                      [minKey]: "",
                      [maxKey]: "",
                    }));
                  }}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 focus:outline-none"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Filter Dialog */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            setIsOpen(false);
            setOpenFilterType(null);
            setFilterSearchQuery(""); // Clear search when closing
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-0 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative flex h-full w-full transform flex-col overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:h-auto sm:max-w-lg sm:rounded-2xl sm:border sm:border-slate-200 sm:p-6">
                  <div className="flex h-full flex-col sm:h-auto">
                    <div className="mb-4 flex items-center justify-between px-4 pt-4 sm:px-0 sm:pt-0">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-slate-900"
                      >
                        {getFilterTitle()}
                      </Dialog.Title>
                      <button
                        type="button"
                        className="rounded-xl bg-white text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:ring-offset-2"
                        onClick={() => {
                          setIsOpen(false);
                          setOpenFilterType(null);
                          setFilterSearchQuery(""); // Clear search when closing
                        }}
                      >
                        <span className="sr-only">{t("Close")}</span>
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    {/* Filter Search Input */}
                    <div className="mb-4 px-4 sm:px-0">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
                        </div>
                        <input
                          type="text"
                          value={filterSearchQuery}
                          onChange={(e) => setFilterSearchQuery(e.target.value)}
                          placeholder={t("Search options...")}
                          className="block w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-3 text-base leading-5 text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200 sm:py-2 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 flex-1 overflow-y-auto px-4 sm:px-0 sm:max-h-96">
                      {/* Show treeview for categories */}
                      {openFilterType === "categories" && categoryTree.length > 0 ? (
                        <>
                          {categoryTree.map((category) => (
                            <CategoryTreeItem
                              key={category.id}
                              category={category}
                              level={0}
                              searchQuery={filterSearchQuery}
                            />
                          ))}
                          {categoryTree.every(cat => !categoryMatchesSearch(cat, filterSearchQuery)) &&
                            filterSearchQuery.trim() && (
                              <div className="px-4 py-8 text-center text-slate-500 sm:px-0">
                                <div className="text-lg sm:text-base">
                                  {t("No categories match your search")}
                                </div>
                              </div>
                            )}
                        </>
                      ) : (
                        <>
                          {getFilterOptions().map((option) => (
                            <label
                              key={option.value}
                              className="flex cursor-pointer items-center rounded-2xl border border-slate-200 p-4 hover:bg-slate-50 sm:p-3"
                            >
                              <input
                                type="checkbox"
                                checked={getTempSelected().includes(option.value)}
                                onChange={() => handleOptionToggle(option.value)}
                                className="h-5 w-5 rounded border-slate-300 text-slate-900 focus:ring-slate-400 sm:h-4 sm:w-4"
                              />
                              <div className="ml-3 flex items-center flex-1">
                                {openFilterType === "status" ? (
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${option.class} sm:text-xs`}
                                  >
                                    {option.label}
                                  </span>
                                ) : (
                                  <span className="text-base font-medium text-slate-900 sm:text-sm">
                                    {option.label}
                                  </span>
                                )}
                              </div>
                              {getTempSelected().includes(option.value) && (
                                <CheckIcon className="ml-auto h-6 w-6 text-slate-900 sm:h-5 sm:w-5" />
                              )}
                            </label>
                          ))}

                          {/* Show message when no options match search */}
                          {getFilterOptions().length === 0 &&
                            filterSearchQuery.trim() && (
                              <div className="px-4 py-8 text-center text-slate-500 sm:px-0">
                                <div className="text-lg sm:text-base">
                                  {t("No options match your search")}
                                </div>
                              </div>
                            )}
                        </>
                      )}
                    </div>

                    <div className="mt-6 flex justify-end space-x-3 px-4 pb-4 sm:mt-6 sm:px-0 sm:pb-0">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-full border border-slate-300 bg-white px-6 py-3 text-base font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200 sm:px-4 sm:py-2 sm:text-sm"
                        onClick={handleResetFilter}
                      >
                        {t("Reset")}
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-full border border-transparent bg-slate-900 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-200 sm:px-4 sm:py-2 sm:text-sm"
                        onClick={handleApplyFilter}
                      >
                        {t("Apply")}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};
